// src/pages/DraftWars/SpectatorView.jsx
// Public read-only view of the Draft Wars session. Stream-friendly.

import { useEffect, useState, useMemo, useRef } from "react";
import { onSnapshot, query, orderBy, limit } from "firebase/firestore";
import { FaGavel, FaClock, FaTrophy, FaCheckCircle } from "react-icons/fa";
import { refs } from "./draftWarsEngine";
import { CAPTAIN_IMAGES, PLAYER_IMAGES, TIER_META } from "./draftWarsData";
import { useCountdown, useServerTimeSync } from "./draftWarsHooks";

const fmt = (n) => "$" + (n ?? 0).toLocaleString();

function SpectatorView() {
  useServerTimeSync();
  const [session, setSession] = useState(null);
  const [captains, setCaptains] = useState([]);
  const [players, setPlayers] = useState([]);
  const [bids, setBids] = useState([]);

  useEffect(() => onSnapshot(refs.session, (s) => setSession(s.exists() ? s.data() : null)), []);
  useEffect(() => onSnapshot(refs.captains, (s) => setCaptains(s.docs.map((d) => ({ id: d.id, ...d.data() })))), []);
  useEffect(() => onSnapshot(refs.players, (s) => setPlayers(s.docs.map((d) => ({ id: d.id, ...d.data() })))), []);
  useEffect(() => {
    const q = query(refs.bids, orderBy("at", "desc"), limit(12));
    return onSnapshot(q, (s) => setBids(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const auction = session?.currentAuction || null;
  const secondsLeft = useCountdown(auction?.timerEndAt);
  const settings = session?.settings || {};

  // ── SOLD splash: show for 3.5s when a new sale is recorded ──
  const [soldSplash, setSoldSplash] = useState(null);
  const prevSaleKeyRef = useRef(undefined); // undefined until the first session snapshot lands

  useEffect(() => {
    if (session === null) return; // still loading

    const sale = session.lastSale;
    const key = sale ? (sale.soldAt?.toMillis?.() ?? sale.soldAt ?? sale.playerId) : null;

    // First snapshot — establish baseline so we don't splash for pre-existing sales
    if (prevSaleKeyRef.current === undefined) {
      prevSaleKeyRef.current = key;
      return;
    }

    // New sale detected
    if (sale && key && key !== prevSaleKeyRef.current) {
      prevSaleKeyRef.current = key;
      setSoldSplash(sale);
    }
  }, [session]);

  // Auto-clear the splash after 3.5s
  useEffect(() => {
    if (!soldSplash) return;
    const t = setTimeout(() => setSoldSplash(null), 3500);
    return () => clearTimeout(t);
  }, [soldSplash]);

  const playersById = useMemo(() => {
    const map = {};
    players.forEach((p) => { map[p.id] = p; });
    return map;
  }, [players]);

  const soldCount = players.filter((p) => p.status === "sold").length;

  if (!session) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center space-y-3">
          <FaGavel className="text-red-600 mx-auto" size={36} />
          <h1 className="text-3xl font-black uppercase text-white">Draft Wars</h1>
          <p className="text-gray-500">No active session — check back soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-8">
      {/* Header */}
      <header className="bg-gradient-to-b from-red-900/30 to-black px-6 py-6 border-b border-red-900/30">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <FaGavel className="text-red-500" size={24} />
            <div>
              <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white">
                Draft <span className="text-red-500">Wars</span>
              </h1>
              <p className="text-xs uppercase tracking-wider text-gray-500">Live Auction · {soldCount}/{players.length} drafted</p>
            </div>
          </div>
          <div>
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest ${
              session.status === "active" ? "bg-green-600/20 text-green-400 border border-green-500/40" :
              session.status === "complete" ? "bg-gray-700/50 text-gray-400 border border-gray-600" :
              "bg-amber-600/20 text-amber-400 border border-amber-500/40"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${session.status === "active" ? "bg-green-500 animate-pulse" : "bg-gray-500"}`} />
              {session.status === "active" ? "Live" : session.status === "complete" ? "Complete" : "Setup"}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Current Auction */}
        <section>
          {soldSplash ? (
            <SoldSplash sale={soldSplash} captains={captains} />
          ) : auction ? (
            <div key={auction.playerId} className={`relative overflow-hidden rounded-3xl border-2 transition-all animate-[fadeIn_0.4s_ease-out] ${
              secondsLeft !== null && secondsLeft <= 2 ? "border-red-500 shadow-2xl shadow-red-500/30" : "border-red-900/40"
            } bg-gradient-to-br from-gray-900 via-gray-900 to-black`}>
              <div className="p-6 md:p-10 grid md:grid-cols-3 gap-6">
                {/* Player */}
                <div className="md:col-span-2 flex items-center gap-5">
                  <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden flex-shrink-0 shadow-xl border-2"
                    style={{ borderColor: (TIER_META[auction.tier]?.color || "#6B7280") + "99" }}>
                    {PLAYER_IMAGES[auction.playerName] ? (
                      <img src={PLAYER_IMAGES[auction.playerName]} alt={auction.playerName}
                        className="w-full h-full object-cover object-top" decoding="async" />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center font-black text-4xl md:text-5xl bg-gradient-to-br ${TIER_META[auction.tier]?.accent || ""} text-white`}>
                        {auction.tier}
                      </div>
                    )}
                    <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-white text-xs font-black shadow-lg"
                      style={{ backgroundColor: TIER_META[auction.tier]?.color }}>
                      {auction.tier}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs uppercase tracking-widest text-red-400">Now Auctioning</span>
                      <span className="px-2 py-0.5 rounded text-white text-[10px] font-black tracking-widest uppercase"
                        style={{ backgroundColor: TIER_META[auction.tier]?.color }}>
                        {TIER_META[auction.tier]?.label || (auction.tier + "-Tier")}
                      </span>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-black text-white truncate leading-none">{auction.playerName}</h2>
                    <p className="text-gray-500 mt-2">Base: {fmt(auction.basePrice)}</p>
                  </div>
                </div>

                {/* Timer */}
                <div className="flex flex-col items-center justify-center text-center bg-black/40 rounded-2xl p-4 border border-gray-800">
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1">
                    <FaClock size={10} /> Timer
                  </p>
                  <p className={`text-6xl md:text-7xl font-black tabular-nums leading-none ${
                    secondsLeft === null ? "text-gray-600" :
                    secondsLeft <= 2 ? "text-red-500 animate-pulse" : "text-white"
                  }`}>
                    {secondsLeft === null ? "—" : secondsLeft}
                  </p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">
                    {secondsLeft === null ? "awaiting first bid" : "seconds"}
                  </p>
                </div>
              </div>

              {/* High bid bar */}
              <div className="bg-black/50 border-t border-red-900/40 px-6 py-5 flex items-center justify-between flex-wrap gap-3">
                {auction.highBid ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-900 border-2 flex-shrink-0"
                        style={{ borderColor: (captains.find((c) => c.id === auction.highBidder)?.color || "#DC2626") + "80" }}>
                        {CAPTAIN_IMAGES[auction.highBidderName] ? (
                          <img src={CAPTAIN_IMAGES[auction.highBidderName]} alt={auction.highBidderName} className="w-full h-full object-cover" decoding="async" />
                        ) : <div className="w-full h-full flex items-center justify-center font-black">{auction.highBidderName?.charAt(0)}</div>}
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500">High bidder</p>
                        <p className="text-xl font-bold text-white">{auction.highBidderName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wider text-gray-500">Bid</p>
                      <p className="text-4xl md:text-5xl font-black text-green-400 tabular-nums leading-none">{fmt(auction.highBid)}</p>
                    </div>
                  </>
                ) : (
                  <p className="text-center w-full text-gray-500">Waiting for first bid · min {fmt(auction.basePrice)}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gray-900/50 border border-dashed border-gray-800 rounded-3xl p-16 text-center space-y-3">
              <FaGavel className="text-gray-700 mx-auto" size={40} />
              <p className="text-gray-500 text-lg">
                {session.status === "complete" ? "🏁 The draft is complete" :
                 session.status === "setup" ? "Draft hasn't started yet" :
                 "Waiting for next player to be nominated…"}
              </p>
            </div>
          )}
        </section>

        {/* Captains grid */}
        <section>
          <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
            <FaTrophy size={11} className="text-red-500" /> Teams
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {captains.map((c) => {
              const roster = (c.roster || []).map((pid) => playersById[pid]).filter(Boolean);
              const img = CAPTAIN_IMAGES[c.name];
              const fullBudget = settings.budget || 120000;
              const pctSpent = Math.max(0, Math.min(100, ((fullBudget - c.budgetRemaining) / fullBudget) * 100));
              const isHighBidder = auction?.highBidder === c.id;
              return (
                <div key={c.id}
                  className={`bg-gray-900 border rounded-2xl p-4 transition-all ${
                    isHighBidder ? "border-green-500/60 shadow-lg shadow-green-500/20" : "border-gray-800"
                  }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-800 border-2 flex-shrink-0" style={{ borderColor: c.color + "80" }}>
                      {img ? <img src={img} alt={c.name} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : <div className="w-full h-full flex items-center justify-center font-black">{c.name.charAt(0)}</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold truncate">{c.name}</h3>
                      <p className="text-xs text-green-400 tabular-nums font-semibold">{fmt(c.budgetRemaining)}</p>
                    </div>
                  </div>
                  <div className="h-1 bg-gray-800 rounded-full overflow-hidden mb-3">
                    <div className="h-full bg-red-600 transition-all" style={{ width: pctSpent + "%" }} />
                  </div>
                  {/* Roster slots */}
                  <div className="space-y-1">
                    {Array.from({ length: settings.rosterSize || 3 }).map((_, i) => {
                      const p = roster[i];
                      if (p) {
                        return (
                          <div key={p.id} className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-800/50 border border-gray-700 rounded text-xs">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <div className="relative w-6 h-6 rounded overflow-hidden flex-shrink-0 border" style={{ borderColor: (TIER_META[p.tier]?.color || "#6B7280") + "80" }}>
                                {PLAYER_IMAGES[p.name] ? (
                                  <img src={PLAYER_IMAGES[p.name]} alt={p.name} className="w-full h-full object-cover object-top" loading="lazy" decoding="async" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center font-black text-[8px]" style={{ backgroundColor: TIER_META[p.tier]?.color, color: "white" }}>{p.tier}</div>
                                )}
                              </div>
                              <span className="font-black text-[10px]" style={{ color: TIER_META[p.tier]?.color }}>{p.tier}</span>
                              <span className="text-white truncate">{p.name}</span>
                            </div>
                            <span className="text-gray-500 tabular-nums text-[10px]">{fmt(p.soldFor)}</span>
                          </div>
                        );
                      }
                      return (
                        <div key={"slot" + i} className="flex items-center gap-2 px-2 py-1 bg-gray-900/50 border border-dashed border-gray-800 rounded text-xs">
                          <span className="text-gray-700">—</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Bid feed */}
        {bids.length > 0 && (
          <section className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-3">Recent Bids</h2>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {bids.map((b) => {
                const cap = captains.find((c) => c.id === b.captainId);
                const pl = players.find((p) => p.id === b.playerId);
                return (
                  <div key={b.id} className="flex items-center justify-between text-sm py-1 border-b border-gray-800/60">
                    <span className="text-gray-400">
                      <span className="text-white font-bold">{cap?.name || "?"}</span> → {pl?.name || "?"}
                    </span>
                    <span className="text-green-400 tabular-nums font-bold">{fmt(b.amount)}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ─── SOLD splash ───────────────────────────────────────────────────────────
// Big "SOLD!" card that takes over the auction slot for 3.5 seconds after a confirm.
function SoldSplash({ sale, captains }) {
  const captain = captains.find((c) => c.id === sale.captainId);
  const captainImg = CAPTAIN_IMAGES[sale.captainName];
  const accent = TIER_META[sale.tier]?.accent || "from-green-500 to-green-700";

  return (
    <div className="relative overflow-hidden rounded-3xl border-2 border-green-500 shadow-2xl shadow-green-500/40 bg-gradient-to-br from-green-950/70 via-gray-900 to-black animate-[fadeIn_0.25s_ease-out]">
      {/* Ambient glow */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle at 20% 30%, rgba(34,197,94,0.35) 0%, transparent 50%),
                          radial-gradient(circle at 80% 70%, rgba(34,197,94,0.25) 0%, transparent 50%)`
      }} />

      <div className="relative p-6 md:p-10">
        <div className="flex items-center gap-2 mb-2">
          <FaCheckCircle className="text-green-400" />
          <span className="text-green-400 text-xs md:text-sm font-black uppercase tracking-widest">Sold!</span>
        </div>

        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-16 h-16 md:w-24 md:h-24 rounded-2xl overflow-hidden flex-shrink-0 shadow-lg border-2"
                style={{ borderColor: (TIER_META[sale.tier]?.color || "#6B7280") + "99" }}>
                {PLAYER_IMAGES[sale.playerName] ? (
                  <img src={PLAYER_IMAGES[sale.playerName]} alt={sale.playerName} className="w-full h-full object-cover object-top" decoding="async" />
                ) : (
                  <div className={`w-full h-full flex items-center justify-center font-black text-3xl md:text-4xl bg-gradient-to-br ${accent} text-white`}>
                    {sale.tier}
                  </div>
                )}
                <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-white text-xs font-black shadow"
                  style={{ backgroundColor: TIER_META[sale.tier]?.color }}>
                  {sale.tier}
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest text-gray-500">Player · <span style={{ color: TIER_META[sale.tier]?.color }}>{TIER_META[sale.tier]?.label || (sale.tier + "-Tier")}</span></p>
                <h2 className="text-3xl md:text-5xl font-black text-white truncate leading-tight">{sale.playerName}</h2>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <p className="text-xs uppercase tracking-widest text-gray-500">Drafted by</p>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-800 border-2 border-green-500/60 flex-shrink-0">
                  {captainImg ? (
                    <img src={captainImg} alt={sale.captainName} className="w-full h-full object-cover" decoding="async" />
                  ) : <div className="w-full h-full flex items-center justify-center font-black text-white" style={{ backgroundColor: captain?.color }}>{sale.captainName?.charAt(0)}</div>}
                </div>
                <span className="text-xl md:text-2xl font-bold text-white">{sale.captainName}</span>
              </div>
            </div>
          </div>

          <div className="text-center md:text-right">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Final Bid</p>
            <p className="text-5xl md:text-7xl font-black text-green-400 tabular-nums leading-none">
              ${sale.amount?.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SpectatorView;
